import React, {useState, useEffect} from 'react';
import {useNavigate, useLocation, useSearchParams} from 'react-router-dom';
import {useAuth} from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext.js';
import ButtonWithLoader from "../../../components/Button/ButtonLoader/ButtonLoader.js";
import { resolvePostLoginPath } from '../../../Utils/postLoginNavigation.js';
import AuthBrand from '../AuthBrand/AuthBrand.jsx';

const TwoFactorVerify = () => {
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [useBackupCode, setUseBackupCode] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const {verify2FA} = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const tempToken = location.state?.tempToken || searchParams.get('tempToken');
    const requiresPasswordChangeAfter2FA =
        !!location.state?.requiresPasswordChange || searchParams.get('requiresPasswordChange') === '1';

    useEffect(() => {
        if (!tempToken) {
            navigate('/login');
        }
    }, [tempToken, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const result = await verify2FA(twoFactorCode, tempToken, useBackupCode);
            navigate(await resolvePostLoginPath({
                passwordChangeRequired: result.requiresPasswordChange,
            }));
        } catch (err) {
            setError(err.response?.data?.message || t('auth.twoFactor.failed'));
        } finally {
            setLoading(false);
        }
    };

    if (!tempToken) {
        return null;
    }

    return (
        <div className="auth-page">
            <AuthBrand corner to="/login" />
            <div className="auth-card auth-card--narrow">
                <div className="auth-verify-header">
                    <p className="auth-eyebrow">{t('auth.twoFactor.eyebrow')}</p>
                    <h1 className="auth-title">{t('auth.twoFactor.title')}</h1>
                    <p className="auth-subtitle auth-verify-subtitle">
                        {useBackupCode
                            ? t('auth.twoFactor.subtitleBackup')
                            : t('auth.twoFactor.subtitleCode')}
                    </p>
                    {requiresPasswordChangeAfter2FA && (
                        <p className="auth-message auth-message--warning" role="status">
                            {t('auth.twoFactor.passwordChangeNotice')}
                        </p>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="auth-verify-form">
                    {error && <div className="auth-message auth-message--error">{error}</div>}

                    <div className="auth-code-input-group">
                        <input
                            type="text"
                            dir="ltr"
                            value={twoFactorCode}
                            onChange={(e) => setTwoFactorCode(e.target.value)}
                            placeholder={useBackupCode ? t('auth.twoFactor.backupPlaceholder') : "000000"}
                            maxLength={useBackupCode ? undefined : "6"}
                            pattern={useBackupCode ? undefined : "[0-9]{6}"}
                            inputMode={useBackupCode ? "text" : "numeric"}
                            autoComplete="one-time-code"
                            className="auth-code-input"
                            required
                            autoFocus
                        />
                    </div>

                    <div className="auth-verify-option">
                        <input
                            type="checkbox"
                            id="useBackupCode"
                            checked={useBackupCode}
                            onChange={() => setUseBackupCode(!useBackupCode)}
                            className="auth-verify-checkbox"
                        />
                        <label htmlFor="useBackupCode" className="auth-verify-checkbox-label">
                            {t('auth.twoFactor.useBackup')}
                        </label>
                    </div>

                    <div className='auth-action-box'>
                        <ButtonWithLoader
                            text={t('auth.twoFactor.submit')}
                            loading={loading}
                            type="submit"
                        />
                    </div>

                    <div className="auth-helper-note">
                        {t('auth.twoFactor.helper')}
                    </div>

                    <button
                        type="button"
                        className="auth-secondary-button"
                        onClick={() => navigate('/login')}
                    >
                        {t('auth.twoFactor.backToLogin')}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default TwoFactorVerify;
